"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { createLogger } from "@/lib/logger";
import { toast } from "sonner";
import { useT } from "@/hooks/useT";
import { useUserMe } from "@/contexts/UserMeContext";
import { usePredictEnabled } from "@/hooks/usePredictEnabled";

const logger = createLogger("Settings");
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  FiCheck,
  FiX,
  FiZap,
  FiExternalLink,
  FiCreditCard,
  FiCopy,
  FiRefreshCw,
  FiAlertTriangle,
  FiLink,
  FiBell,
  FiGlobe,
  FiEyeOff,
  FiKey,
  FiTrendingUp,
  FiActivity,
  FiMail,
  FiPlus,
  FiTarget,
  FiStar,
} from "react-icons/fi";
import { WalletIcon } from "@/app/components/icons/WalletIcon";
import { CopyIcon } from "@/app/components/icons/CopyIcon";
import { LinkIcon } from "@/app/components/icons/LinkIcon";
import { FaTelegram } from "react-icons/fa6";
import { useEnableTrading } from "@/hooks/useEnableTrading";
import { usePrivy, useLinkAccount, useSigners } from "@privy-io/react-auth";
import { useExportWallet } from "@privy-io/react-auth/extended-chains";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useSolanaBalanceContext } from "@/contexts/SolanaBalanceContext";
import { useBnbBalance } from "@/hooks/useBnbBalance";
import { useProofKYC } from "@/hooks/useProofKYC";
import UnifiedBalanceCard from "@/components/bridge/UnifiedBalanceCard";
import MoveFundsModal from "@/components/bridge/MoveFundsModal";
import EoaSweepInline from "@/app/components/EoaSweepInline";
import { STATIC_ASSETS } from "@/lib/cdn";
import { useNewsNotifications } from "@/contexts/NewsNotificationsContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { FiRss } from "react-icons/fi";

type Section = "trading" | "notifications" | "wallet" | "connections";

const VALID_SECTIONS: Section[] = ["trading", "notifications", "wallet", "connections"];

export default function SettingsPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-400 dark:border-gray-500 border-t-transparent" />
      </div>
    }>
      <SettingsContent />
    </Suspense>
  );
}

/** Map Privy's `linkMethod` enum (twitter, google_oauth, telegram, etc.)
 *  to a human label for toasts. Privy ships a few different shapes
 *  across versions (twitter_oauth vs twitter, etc.), so we string-match
 *  loosely instead of typing strictly. */
function linkMethodLabel(linkMethod: string): string {
  const m = linkMethod.toLowerCase();
  if (m.includes("twitter") || m === "x") return "X (Twitter)";
  if (m.includes("google")) return "Google";
  if (m.includes("telegram")) return "Telegram";
  if (m.includes("email")) return "Email";
  if (m.includes("wallet") || m === "siwe" || m === "siws") return "Wallet";
  if (m.includes("discord")) return "Discord";
  if (m.includes("farcaster")) return "Farcaster";
  if (m.includes("apple")) return "Apple";
  return "Account";
}

function SettingsContent() {
  const t = useT();
  const { isLoading, isReady, isLoggedIn, currentWalletAddress } = useAuth();
  // /api/users/me is already cached + revalidated by UserMeContext (mounted
  // app-wide). Reading from it instead of doing our own fetch lets the page
  // paint immediately on revisits and avoids a duplicate HTTP round-trip on
  // every Settings visit.
  const { user: userMe, loading: userMeLoading } = useUserMe();
  const { user } = usePrivy();
  // Wire onSuccess/onError so a Privy linking failure surfaces a toast
  // instead of dying silently in the console. Without this, the
  // "Connect X (Twitter)" button looked broken when Privy returned
  // "account already linked", popup-blocker, or OAuth-config errors.
  const { linkGoogle, linkTwitter, linkTelegram, linkEmail, linkWallet } = useLinkAccount({
    onSuccess: ({ linkMethod }) => {
      const label = linkMethodLabel(String(linkMethod || ""));
      toast.success(t("settings.toast.connected", { label }));
    },
    onError: (errorCode, details) => {
      const linkMethod = String((details as any)?.linkMethod || "");
      const label = linkMethodLabel(linkMethod);
      toast.error(t("settings.toast.connectFailed", { label, error: String(errorCode) }));
      logger.error("useLinkAccount error", { errorCode: String(errorCode), details });
    },
  });
  const { tradingEnabled, safeAddress, depositWalletAddress, refetch: refetchBalance } = usePolymarketBalanceContext();
  // Display address precedence: deposit wallet → safe → null. EOA fallback
  // intentionally NOT included for the Polymarket card — funds don't live on
  // the EOA for any custody model, so showing it would mislead users.
  const polymarketTradingAddress = depositWalletAddress || safeAddress || null;
  const { solanaAddress, balance: solanaBalance, solBalance } = useSolanaBalanceContext();
  const { balance: polygonBalance, polBalance } = usePolymarketBalanceContext();
  const { walletAddress: bnbWalletAddress, usdtBalance: bnbUsdtBalance, bnbBalance } = useBnbBalance();
  const predictEnabled = usePredictEnabled();
  const { verified: kalshiKycVerified, loading: kalshiKycLoading, startVerification: startKalshiKyc, accountStatus: kalshiAccountStatus, refresh: refreshKalshiStatus } = useProofKYC();
  const kalshiAccountActive = kalshiAccountStatus === 'active';
  const [kalshiKycStarting, setKalshiKycStarting] = useState(false);
  const [kalshiReenabling, setKalshiReenabling] = useState(false);
  const { addSigners, removeSigners } = useSigners();
  const { exportWallet } = useExportWallet();
  const { isEnabled: newsNotificationsEnabled, setIsEnabled: setNewsNotificationsEnabled } = useNewsNotifications();
  const { state: pushState, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe, isLoading: pushLoading, isSupported: pushSupported, error: pushError } = usePushNotifications();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Read initial tab from URL
  const tabFromUrl = searchParams.get("tab") as Section | null;
  const initialTab = tabFromUrl && VALID_SECTIONS.includes(tabFromUrl) ? tabFromUrl : "trading";
  const [activeSection, setActiveSection] = useState<Section>(initialTab);

  // 2026-05-22: Brave detection so we can show a tailored hint when
  // pushManager.subscribe() fails. Brave disables Google FCM by default
  // (the push service every WebPush implementation falls back to) — the
  // resulting error string "Registration failed - push service error"
  // is opaque; users assume the app is broken. Detection uses
  // navigator.brave?.isBrave() which is the only reliable signal —
  // Brave spoofs the userAgent as Chrome on purpose, so UA sniffing
  // won't work.
  const [isBrave, setIsBrave] = useState(false);
  useEffect(() => {
    (async () => {
      const nav = (typeof navigator !== "undefined" ? (navigator as any) : null);
      if (nav?.brave && typeof nav.brave.isBrave === "function") {
        try {
          const result = await nav.brave.isBrave();
          if (result) setIsBrave(true);
        } catch { /* ignore — fail open as "not Brave" */ }
      }
    })();
  }, []);

  // Update URL when tab changes
  const handleSectionChange = (section: Section) => {
    setActiveSection(section);
    const newUrl = section === "trading" ? "/settings" : `/settings?tab=${section}`;
    router.replace(newUrl, { scroll: false });
  };

  // Sync state when URL changes (e.g., browser back/forward)
  // Only depend on searchParams — NOT activeSection (avoids feedback loop)
  useEffect(() => {
    const tabParam = searchParams.get("tab") as Section | null;
    const targetTab = tabParam && VALID_SECTIONS.includes(tabParam) ? tabParam : "trading";
    setActiveSection(targetTab);
  }, [searchParams]);

  // UI state
  // Only show the page-wide skeleton until we have *something* — userMe
  // arrives synchronously from localStorage on revisits (initialData in
  // useUserMeQuery), so this is true at most for the first ms on a cold
  // signup before the seed lands.
  const [loading, setLoading] = useState(true);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [walletCopyHovered, setWalletCopyHovered] = useState(false);
  const [copiedSafe, setCopiedSafe] = useState(false);
  const [safeCopyHovered, setSafeCopyHovered] = useState(false);
  const [copiedSolWallet, setCopiedSolWallet] = useState(false);
  const [copiedBnbWallet, setCopiedBnbWallet] = useState(false);
  const [showMoveFundsModal, setShowMoveFundsModal] = useState(false);
  const [bnbWalletCopyHovered, setBnbWalletCopyHovered] = useState(false);

  // Trading state
  const [revoking, setRevoking] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState<string | null>(null); // 'polymarket' | 'kalshi' | null
  const [autoRedeem, setAutoRedeem] = useState(false);
  const [autoRedeemLoading, setAutoRedeemLoading] = useState(false);
  const [slippagePct, setSlippagePct] = useState(5);
  const [slippageSaving, setSlippageSaving] = useState(false);
  // Per-source slippage overrides. Each tab edits its own bucket;
  // "global" edits the legacy default_slippage_pct fallback. Persisted
  // server-side in users.slippage_per_source (see migration 214).
  type SlippageTab = 'global' | 'polymarket' | 'hyperliquid' | 'kalshi' | 'predict';
  const [slippageTab, setSlippageTab] = useState<SlippageTab>('global');
  const [slippagePerSource, setSlippagePerSource] = useState<Partial<Record<Exclude<SlippageTab, 'global'>, number>>>({});
  // 2026-05-27 (Pika): inline "Custom" input for slippage values
  // beyond the slider's 0.5-20% range. Backend accepts 0.1-100
  // (users.ts:1828). The slider stays capped at 20 for the typical UX;
  // power users (penny-market copy traders, fast HL outcomes) reach
  // higher values via this input. Per-tab state is implicit — when
  // the user switches tabs we close the input.
  const [slippageCustomOpen, setSlippageCustomOpen] = useState(false);
  const [slippageCustomDraft, setSlippageCustomDraft] = useState("");
  useEffect(() => {
    setSlippageCustomOpen(false);
  }, [slippageTab]);
  const [quickBuyInputs, setQuickBuyInputs] = useState<[string, string, string]>(["", "", ""]);
  const [quickBuySaving, setQuickBuySaving] = useState(false);
  // 2026-05-22 (Pika): track the last saved snapshot so the Save
  // button can show a "dirty" state — was permanently grey
  // before, even with pending changes. Compared as a tuple-equals
  // so empty strings collapse correctly.
  const [quickBuySaved, setQuickBuySaved] = useState<[string, string, string]>(["", "", ""]);
  // Polymarket market-order time-in-force. Mirrors the Trading Settings
  // panel on polymarket.com. Defaults to FOK (all-or-nothing) so market
  // orders behave the way most traders expect: the price you see or nothing.
  const [marketOrderType, setMarketOrderType] = useState<'FOK' | 'FAK'>('FOK');
  const [marketOrderTypeSaving, setMarketOrderTypeSaving] = useState(false);

  // Polymarket enable trading (uses the full Safe deploy + CLOB flow)
  const {
    isLoading: polyEnabling,
    isEnabled: polyEnabled,
    error: polyEnableError,
    stepMessage: polyStepMessage,
    enableTrading: polyEnableTrading,
    reset: polyReset,
  } = useEnableTrading();

  // Predict enable state
  const [predictEnabling, setPredictEnabling] = useState(false);
  const [predictEnableError, setPredictEnableError] = useState<string | null>(null);
  const [predictEnableStep, setPredictEnableStep] = useState<string | null>(null);

  // Predict trading state
  const [predictTradingReady, setPredictTradingReady] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isReady || !isLoggedIn) return;
    let mounted = true;
    (async () => {
      try {
        const res = await authFetch("/api/trading/predict/status");
        if (!res.ok || !mounted) return;
        const data = await res.json();
        if (mounted) setPredictTradingReady(data.ready === true);
      } catch {
        if (mounted) setPredictTradingReady(false);
      }
    })();
    return () => { mounted = false; };
  }, [isReady, isLoggedIn]);

  // Hyperliquid trading state. The status endpoint returns `ready` true
  // only when both agent + (optional) builder are approved AND there's a
  // non-zero HyperCore balance. We surface "active" once the agent is
  // approved (whether or not they've deposited yet) since that's the
  // permanent enable step — deposits live elsewhere in the modal.
  const [hlAgentApproved, setHlAgentApproved] = useState<boolean | null>(null);
  const [hlReady, setHlReady] = useState<boolean | null>(null);
  const [hlBalance, setHlBalance] = useState<number>(0);
  const refreshHlStatus = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await authFetch("/api/trading/hyperliquid/status");
      if (!res.ok) return;
      const data = await res.json();
      setHlAgentApproved(!!data.agentApproved);
      setHlReady(!!data.ready);
      setHlBalance(Number(data.hyperCoreBalanceUsd || 0));
    } catch {
      /* ignore */
    }
  }, [isLoggedIn]);
  useEffect(() => {
    if (!isReady || !isLoggedIn) return;
    void refreshHlStatus();
  }, [isReady, isLoggedIn, refreshHlStatus]);
  // Re-poll when the user closes the HL onboarding modal (it dispatches
  // 'frenflow:hl-status-refresh' after each step completes).
  useEffect(() => {
    const handler = () => void refreshHlStatus();
    if (typeof window !== 'undefined') {
      window.addEventListener('frenflow:hl-status-refresh', handler);
      return () => window.removeEventListener('frenflow:hl-status-refresh', handler);
    }
  }, [refreshHlStatus]);

  // Notification settings state
  const [notifications, setNotifications] = useState({
    copy_trade: true,
    copy_skips: true,
    limit_filled: true,
    limit_expired: true,
    position_resolved: true,
    low_balance: true,
    high_slippage: true,
  });
  const [notificationLoading, setNotificationLoading] = useState<string | null>(null);

  // Additional settings
  const [webNotificationsEnabled, setWebNotificationsEnabled] = useState<boolean | null>(null);
  const [ninjaMode, setNinjaMode] = useState<boolean | null>(null);
  const [showTicker, setShowTicker] = useState<boolean | null>(null);
  const [webBellCopytrading, setWebBellCopytrading] = useState<boolean | null>(null);
  const [webBellWalletTracker, setWebBellWalletTracker] = useState<boolean | null>(null);
  const [webBellPriceAlerts, setWebBellPriceAlerts] = useState<boolean | null>(null);
  const [additionalSettingsLoading, setAdditionalSettingsLoading] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (isReady && !isLoggedIn) {
      router.push("/");
    }
  }, [isReady, isLoggedIn, router]);

  // Hydrate local state from userMe whenever it arrives or changes. Removed
  // the standalone /api/users/me fetch + page-wide `loading=true` blocker —
  // UserMeContext seeds from localStorage on mount (`initialData` in
  // useUserMeQuery), so the form fields are pre-populated on the very first
  // render and any background refetch silently updates them in place.
  useEffect(() => {
    if (!userMe) {
      // Only clear the blocker once Privy itself has resolved — otherwise
      // a quick "userMe=null" tick during auth init would flicker the
      // skeleton off and on.
      if (!userMeLoading) setLoading(false);
      return;
    }

    const u = userMe as any;
    setAutoRedeem(u.auto_redeem ?? false);
    setSlippagePct(u.default_slippage_pct ?? 5);
    const ps = u.slippage_per_source;
    if (ps && typeof ps === 'object') {
      const cleaned: Partial<Record<'polymarket' | 'hyperliquid' | 'kalshi' | 'predict', number>> = {};
      for (const k of ['polymarket', 'hyperliquid', 'kalshi', 'predict'] as const) {
        const v = Number(ps[k]);
        if (Number.isFinite(v) && v > 0) cleaned[k] = v;
      }
      setSlippagePerSource(cleaned);
    }
    setNotifications({
      copy_trade: u.notify_copy_trade ?? true,
      // mig 272 — separate from copy_trade so users can mute the
      // "Trade Copied" spam without losing the diagnostic alerts
      // that explain why some copies aren't landing.
      copy_skips: (u as any).notify_copy_skips ?? true,
      limit_filled: u.notify_limit_filled ?? true,
      limit_expired: u.notify_limit_expired ?? true,
      position_resolved: u.notify_position_resolved ?? true,
      low_balance: u.notify_low_balance ?? true,
      high_slippage: u.notify_high_slippage ?? true,
    });
    setWebNotificationsEnabled(u.notify_web_enabled ?? true);
    setNinjaMode(u.ninja_mode_enabled ?? false);
    setWebBellCopytrading(u.notify_web_copytrading ?? true);
    setWebBellWalletTracker(u.notify_web_wallet_tracker ?? true);
    setWebBellPriceAlerts(u.notify_web_price_alerts ?? true);
    setShowTicker(u.show_ticker ?? true);
    setMarketOrderType(u.market_order_type === 'FAK' ? 'FAK' : 'FOK');
    const qba = u.quick_buy_amounts;
    if (qba && Array.isArray(qba) && qba.length > 0) {
      const initial: [string, string, string] = [
        qba[0] ? String(qba[0]) : "",
        qba[1] ? String(qba[1]) : "",
        qba[2] ? String(qba[2]) : "",
      ];
      setQuickBuyInputs(initial);
      setQuickBuySaved(initial);
    }
    setLoading(false);
  }, [userMe, userMeLoading]);

  // Quick Buy dirty detection — Save button is only "primary" when
  // the inputs differ from the last saved snapshot. Matches the
  // pattern users expect from settings forms (no-op clicks are
  // disabled).
  const quickBuyDirty =
    quickBuyInputs[0] !== quickBuySaved[0] ||
    quickBuyInputs[1] !== quickBuySaved[1] ||
    quickBuyInputs[2] !== quickBuySaved[2];

  // Toggle notification setting
  const handleNotificationToggle = async (key: keyof typeof notifications) => {
    setNotificationLoading(key);
    try {
      const newValue = !notifications[key];
      const res = await authFetch("/api/users/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [`notify_${key}`]: newValue }),
      });
      if (res.ok) {
        setNotifications(prev => ({ ...prev, [key]: newValue }));
      } else {
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error(`Failed to update notification ${key}`, { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setNotificationLoading(null);
    }
  };

  // Toggle additional settings
  const handleAdditionalSettingToggle = async (setting: 'web' | 'ninja' | 'ticker' | 'bell_copytrading' | 'bell_wallet_tracker' | 'bell_price_alerts') => {
    setAdditionalSettingsLoading(setting);
    try {
      let fieldName: string;
      let newValue: boolean;
      let setter: (v: boolean) => void;

      switch (setting) {
        case 'web':
          fieldName = 'notify_web_enabled';
          newValue = !(webNotificationsEnabled ?? true);
          setter = setWebNotificationsEnabled;
          break;
        case 'ninja':
          fieldName = 'ninja_mode_enabled';
          newValue = !(ninjaMode ?? false);
          setter = setNinjaMode;
          break;
        case 'ticker':
          fieldName = 'show_ticker';
          newValue = !(showTicker ?? true);
          setter = setShowTicker;
          break;
        case 'bell_copytrading':
          fieldName = 'notify_web_copytrading';
          newValue = !(webBellCopytrading ?? true);
          setter = setWebBellCopytrading;
          break;
        case 'bell_wallet_tracker':
          fieldName = 'notify_web_wallet_tracker';
          newValue = !(webBellWalletTracker ?? true);
          setter = setWebBellWalletTracker;
          break;
        case 'bell_price_alerts':
          fieldName = 'notify_web_price_alerts';
          newValue = !(webBellPriceAlerts ?? true);
          setter = setWebBellPriceAlerts;
          break;
      }

      const res = await authFetch("/api/users/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [fieldName]: newValue }),
      });

      if (res.ok) {
        setter(newValue);
        // Dispatch custom event so LiveTicker can react immediately
        if (setting === 'ticker') {
          window.dispatchEvent(new CustomEvent('tickerVisibilityChange', { detail: newValue }));
        }
      } else {
        const data = await res.json().catch(() => ({}));
        logger.error(`Failed to update ${setting}`, { error: data.error || 'Unknown error' });
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error(`Failed to update ${setting}`, { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setAdditionalSettingsLoading(null);
    }
  };

  // Trading handlers
  const handleRevoke = async (platform: string) => {
    setRevoking(true);
    try {
      if (platform === 'polymarket') {
        const res = await authFetch("/api/trading/revoke", { method: "POST" });
        if (!res.ok) throw new Error("Failed to revoke Polymarket");
      } else if (platform === 'kalshi') {
        const res = await authFetch("/api/dflow/revoke", { method: "POST" });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to revoke Kalshi");
        const addr = data.solanaAddress || solanaAddress;
        if (addr) {
          try { await removeSigners({ address: addr }); } catch {}
        }
      } else if (platform === 'predict') {
        const res = await authFetch("/api/trading/predict/revoke", { method: "POST" });
        if (!res.ok) throw new Error("Failed to revoke Predict.fun");
      }
      setShowRevokeConfirm(null);
      await new Promise(resolve => setTimeout(resolve, 500));
      window.location.reload();
    } catch (err) {
      logger.error("Revoke error", { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setRevoking(false);
    }
  };

  const handleSlippageSave = async (value: number, source?: 'polymarket' | 'hyperliquid' | 'kalshi' | 'predict') => {
    setSlippageSaving(true);
    try {
      const res = await authFetch("/api/users/slippage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(source ? { slippagePct: value, source } : { slippagePct: value }),
      });
      if (res.ok) {
        if (source) {
          // Optimistic local update — don't drop other source overrides.
          setSlippagePerSource((prev) => ({ ...prev, [source]: value }));
          const label = source === 'polymarket' ? 'Polymarket'
            : source === 'hyperliquid' ? 'Hyperliquid'
            : source === 'kalshi' ? 'Kalshi'
            : 'Predict';
          toast.success(t("settings.toast.slippageSetSource", { label, value }));
        } else {
          setSlippagePct(value);
          toast.success(t("settings.toast.slippageSet", { value }));
        }
      } else {
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error("Failed to update slippage", { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setSlippageSaving(false);
    }
  };

  const handleQuickBuySave = async () => {
    setQuickBuySaving(true);
    try {
      const amounts = quickBuyInputs
        .map(s => parseInt(s.trim().replace(/^\$/, ""), 10))
        .filter(n => !isNaN(n) && n >= 1 && n <= 10000);

      const payload = amounts.length > 0 ? amounts : null;
      const res = await authFetch("/api/users/quick-buy-amounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amounts: payload }),
      });
      if (res.ok) {
        const data = await res.json();
        const saved: number[] = data.quick_buy_amounts || [];
        const next: [string, string, string] = [
          saved[0] ? String(saved[0]) : "",
          saved[1] ? String(saved[1]) : "",
          saved[2] ? String(saved[2]) : "",
        ];
        setQuickBuyInputs(next);
        setQuickBuySaved(next);
        toast.success(t("settings.toast.quickBuySaved"));
      } else {
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error("Failed to update quick buy amounts", { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setQuickBuySaving(false);
    }
  };

  const handleMarketOrderTypeChange = async (next: 'FOK' | 'FAK') => {
    if (next === marketOrderType) return;
    setMarketOrderTypeSaving(true);
    try {
      const res = await authFetch("/api/users/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ market_order_type: next }),
      });
      if (res.ok) {
        setMarketOrderType(next);
        toast.success(t("settings.toast.orderTypeSet", { type: next === 'FOK' ? t("settings.toast.orderTypeFok") : t("settings.toast.orderTypeFak") }));
      } else {
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error("Failed to update market_order_type", { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setMarketOrderTypeSaving(false);
    }
  };

  const handleAutoRedeemToggle = async () => {
    setAutoRedeemLoading(true);
    try {
      const newValue = !autoRedeem;
      const res = await authFetch("/api/users/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auto_redeem: newValue }),
      });
      if (res.ok) {
        setAutoRedeem(newValue);
        toast.success(newValue ? t("settings.toast.autoRedeemOn") : t("settings.toast.autoRedeemOff"));
      } else {
        toast.error(t("settings.toast.updateFailed"));
      }
    } catch (err) {
      logger.error("Failed to update auto_redeem", { error: err instanceof Error ? err.message : String(err) });
      toast.error(t("settings.toast.updateFailed"));
    } finally {
      setAutoRedeemLoading(false);
    }
  };

  const copyWalletAddress = () => {
    if (currentWalletAddress) {
      navigator.clipboard.writeText(currentWalletAddress);
      setCopiedWallet(true);
      setTimeout(() => setCopiedWallet(false), 2000);
    }
  };

  const truncateAddress = (address: string) => `${address.slice(0, 6)}...${address.slice(-4)}`;

  if (isLoading || loading) {
    return (
      <div className="w-full max-w-5xl mx-auto pt-4 pb-24">
        {/* Tab bar placeholder — reserves height to prevent CLS when content hydrates */}
        <div className="h-11 mb-4 bg-gray-100 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] animate-pulse" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-400 dark:border-gray-500 border-t-transparent" />
        </div>
      </div>
    );
  }

  const navItems: { id: Section; label: string; icon: React.ReactNode }[] = [
    { id: "trading", label: t("settings.nav.trading"), icon: <FiZap className="w-4 h-4" /> },
    { id: "wallet", label: t("settings.nav.wallet"), icon: <FiCreditCard className="w-4 h-4" /> },
    { id: "notifications", label: t("settings.nav.notifications"), icon: <FiBell className="w-4 h-4" /> },
    { id: "connections", label: t("settings.nav.connections"), icon: <FiLink className="w-4 h-4" /> },
  ];

  const activeNavLabel = navItems.find((i) => i.id === activeSection)?.label ?? t("settings.title");

  return (
    <div className="w-full max-w-6xl mx-auto pt-6 pb-16">
      {/* 2026-05-22 (Pika): removed the giant H1 "Settings" because
          the same word already shows up in the global topbar AND in
          the per-tab H2 inside the content column. Three "Settings"
          stacked on top of each other pushed the actual content
          below the fold on a 14" laptop. The subtitle moves under
          the contextual H2 of each tab so it stays in scope. */}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-10">
        {/* Left rail — vertical nav */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          {/* Mobile horizontal pill fallback */}
          <div className="flex lg:hidden gap-1.5 overflow-x-auto scrollbar-hide pb-2">
            {navItems.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => handleSectionChange(item.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] font-inter-tight font-medium text-sm whitespace-nowrap transition-colors ${
                  activeSection === item.id
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    : "bg-gray-100 dark:bg-[#18181D] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Desktop vertical rail */}
          <nav className="hidden lg:flex flex-col gap-0.5">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  className={`group flex items-center gap-2.5 px-3 py-2 rounded-[5px] font-inter-tight font-medium text-sm transition-colors text-left ${
                    isActive
                      ? "bg-gray-100 dark:bg-[#18181D] text-gray-900 dark:text-white"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#18181D]/60"
                  }`}
                >
                  <span className={isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300"}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content column */}
        <div className="min-w-0 max-w-3xl">
          <div className="mb-4 lg:mb-6 hidden lg:block">
            <h2 className="font-inter-tight text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">{activeNavLabel}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("settings.subtitle")}</p>
          </div>

        {/* TRADING SECTION */}
        {activeSection === "trading" && (
          <div className="space-y-3 lg:space-y-4">
            {/* Trading Status - Platform status with checks */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12]">
                  <FiZap className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.trading.tradingStatus")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t("settings.trading.platformConnections")}</p>
                </div>
              </div>

              {/* Platform status rows */}
              <div className="space-y-2 mb-4">
                {/* Polymarket */}
                <div className="p-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <img src="/logos/polymarket/icon-white.png" alt="Polymarket" width={20} height={20} className="w-5 h-5 rounded-[5px] bg-[#2E5CFF] p-0.5" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Polymarket</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!tradingEnabled && !polyEnabling && !polyEnabled && (
                        <button
                          onClick={async () => { polyReset(); await polyEnableTrading(); }}
                          className="px-4 py-1.5 text-xs font-bold text-white bg-[#2E5CFF] rounded-[5px] hover:bg-[#2E5CFF]/80 transition-colors"
                        >
                          {t("settings.trading.enable")}
                        </button>
                      )}
                      {polyEnabling && (
                        <span className="px-3 py-1 text-[10px] font-medium text-[#2E5CFF] bg-[#2E5CFF]/10 rounded-[5px] animate-pulse">
                          {polyStepMessage || t("settings.trading.enabling")}
                        </span>
                      )}
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] ${(tradingEnabled || polyEnabled) ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <div className={`w-1.5 h-1.5 rounded-[5px] ${(tradingEnabled || polyEnabled) ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`text-[10px] font-medium ${(tradingEnabled || polyEnabled) ? "text-green-500" : "text-red-500"}`}>
                          {(tradingEnabled || polyEnabled) ? t("settings.trading.active") : t("settings.trading.inactive")}
                        </span>
                      </div>
                      {(tradingEnabled || polyEnabled) && showRevokeConfirm !== 'polymarket' && (
                        <button
                          onClick={() => setShowRevokeConfirm('polymarket')}
                          className="px-2 py-0.5 text-[10px] font-medium text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        >
                          {t("settings.trading.revoke")}
                        </button>
                      )}
                    </div>
                  </div>
                  {polyEnableError && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px]">
                      <span className="text-[11px] text-red-500">{polyEnableError}</span>
                    </div>
                  )}
                  {showRevokeConfirm === 'polymarket' && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px] flex items-center justify-between">
                      <span className="text-[11px] text-red-500">{t("settings.trading.deletePolyCreds")}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => setShowRevokeConfirm(null)} className="px-2 py-1 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded">{t("settings.trading.cancel")}</button>
                        <button onClick={() => handleRevoke('polymarket')} disabled={revoking} className="px-2 py-1 text-[10px] font-medium bg-red-500 text-white rounded disabled:opacity-50">{revoking ? "..." : t("settings.trading.revoke")}</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Predict.fun — gated by ENABLE_PREDICT (NEXT_PUBLIC_ENABLE_PREDICT=true). */}
                {predictEnabled && (
                <div className="p-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-[5px] bg-[#6366F1] flex items-center justify-center">
                        <span className="text-[9px] font-black text-black leading-none">P</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Predict.fun</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {predictTradingReady === false && !predictEnabling && (
                        <button
                          onClick={async () => {
                            setPredictEnabling(true);
                            setPredictEnableError(null);
                            setPredictEnableStep("Updating wallet policy...");
                            try {
                              // Step 1: Get latest policy config from backend
                              const configRes = await authFetch("/api/trading/prepare");
                              const config = await configRes.json();
                              if (config.authorizationKeyId && config.policyId) {
                                // Step 2: Update wallet signers with single EVM policy
                                const addr = user?.wallet?.address;
                                if (addr) {
                                  try { await removeSigners({ address: addr }); } catch {}
                                  await addSigners({
                                    address: addr,
                                    signers: [{ signerId: config.authorizationKeyId, policyIds: [config.policyId] }],
                                  });
                                }
                              }
                              // Step 3: Call predict enable
                              setPredictEnableStep("Approving contracts...");
                              const res = await authFetch("/api/trading/predict/enable", { method: "POST" });
                              const data = await res.json();
                              if (data.success) {
                                setPredictTradingReady(true);
                                setPredictEnableStep(null);
                                toast.success("Predict.fun trading enabled!");
                              } else {
                                setPredictEnableError(data.error || "Enable failed");
                                setPredictEnableStep(null);
                              }
                            } catch (err: any) {
                              setPredictEnableError(err.message || "Enable failed");
                              setPredictEnableStep(null);
                            } finally {
                              setPredictEnabling(false);
                            }
                          }}
                          className="px-4 py-1.5 text-xs font-bold text-white bg-[#6366F1] rounded-[5px] hover:bg-[#6366F1]/80 transition-colors"
                        >
                          {t("settings.trading.enable")}
                        </button>
                      )}
                      {predictEnabling && (
                        <span className="px-3 py-1 text-[10px] font-medium text-[#6366F1] bg-[#6366F1]/10 rounded-[5px] animate-pulse">
                          {predictEnableStep || t("settings.trading.enabling")}
                        </span>
                      )}
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] ${predictTradingReady ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <div className={`w-1.5 h-1.5 rounded-[5px] ${predictTradingReady ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`text-[10px] font-medium ${predictTradingReady ? "text-green-500" : "text-red-500"}`}>
                          {predictTradingReady === null ? "..." : predictTradingReady ? t("settings.trading.active") : t("settings.trading.inactive")}
                        </span>
                      </div>
                      {predictTradingReady && showRevokeConfirm !== 'predict' && (
                        <button
                          onClick={() => setShowRevokeConfirm('predict')}
                          className="px-2 py-0.5 text-[10px] font-medium text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        >
                          {t("settings.trading.revoke")}
                        </button>
                      )}
                    </div>
                  </div>
                  {predictEnableError && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px]">
                      <span className="text-[11px] text-red-500">{predictEnableError}</span>
                    </div>
                  )}
                  {showRevokeConfirm === 'predict' && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px] flex items-center justify-between">
                      <span className="text-[11px] text-red-500">{t("settings.trading.revokePredict")}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => setShowRevokeConfirm(null)} className="px-2 py-1 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded">{t("settings.trading.cancel")}</button>
                        <button onClick={() => handleRevoke('predict')} disabled={revoking} className="px-2 py-1 text-[10px] font-medium bg-red-500 text-white rounded disabled:opacity-50">{revoking ? "..." : t("settings.trading.revoke")}</button>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Kalshi — requires DFlow Proof KYC */}
                <div className="p-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <img src="/logos/kalshi/k-icon.svg" alt="Kalshi" className="w-5 h-5 rounded-[5px] bg-[#09C285] p-0.5" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Kalshi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!kalshiKycLoading && !kalshiKycVerified && !kalshiKycStarting && (
                        <button
                          onClick={async () => {
                            setKalshiKycStarting(true);
                            try {
                              await startKalshiKyc();
                            } catch {
                              setKalshiKycStarting(false);
                              toast.error(t("settings.toast.kycFailed"));
                            }
                          }}
                          className="px-4 py-1.5 text-xs font-bold text-white bg-[#09C285] rounded-[5px] hover:bg-[#09C285]/80 transition-colors"
                        >
                          {t("settings.trading.verifyKyc")}
                        </button>
                      )}
                      {kalshiKycStarting && (
                        <span className="px-3 py-1 text-[10px] font-medium text-[#09C285] bg-[#09C285]/10 rounded-[5px] animate-pulse">
                          {t("settings.trading.signing")}
                        </span>
                      )}
                      {kalshiKycVerified && (kalshiAccountStatus === 'disabled' || searchParams.get("kalshi_reenable") === "true") && (
                        <button
                          disabled={kalshiReenabling}
                          onClick={async () => {
                            setKalshiReenabling(true);
                            try {
                              const res = await authFetch("/api/dflow/re-enable", { method: "POST" });
                              const data = await res.json();
                              if (!data.success) throw new Error(data.error || "Backend error");
                              const solAddr = data.solanaAddress || solanaAddress;
                              if (!solAddr) throw new Error("No Solana wallet found — please refresh the page");
                              try { await removeSigners({ address: solAddr }); } catch {}
                              if (data.solanaPolicyId) {
                                await addSigners({
                                  address: solAddr,
                                  signers: [{ signerId: data.authorizationKeyId, policyIds: [data.solanaPolicyId] }],
                                });
                              }
                              toast.success(t("settings.toast.kalshiReenabled"));
                              refreshKalshiStatus();
                            } catch (err: any) {
                              toast.error(err.message || t("settings.toast.reenableFailed"));
                            } finally {
                              setKalshiReenabling(false);
                            }
                          }}
                          className="px-2.5 py-1 text-[10px] font-bold text-[#09C285] bg-[#09C285]/10 rounded-[5px] hover:bg-[#09C285]/20 transition-colors disabled:opacity-50"
                        >
                          {kalshiReenabling ? t("settings.trading.updating") : t("settings.trading.reenable")}
                        </button>
                      )}
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] ${kalshiAccountActive ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <div className={`w-1.5 h-1.5 rounded-[5px] ${kalshiAccountActive ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`text-[10px] font-medium ${kalshiAccountActive ? "text-green-500" : "text-red-500"}`}>
                          {kalshiKycLoading ? "..." : kalshiAccountActive ? t("settings.trading.active") : t("settings.trading.inactive")}
                        </span>
                      </div>
                      {kalshiAccountActive && showRevokeConfirm !== 'kalshi' && (
                        <button
                          onClick={() => setShowRevokeConfirm('kalshi')}
                          className="px-2 py-0.5 text-[10px] font-medium text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        >
                          {t("settings.trading.revoke")}
                        </button>
                      )}
                    </div>
                  </div>
                  {showRevokeConfirm === 'kalshi' && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px] flex items-center justify-between">
                      <span className="text-[11px] text-red-500">{t("settings.trading.revokeKalshi")}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => setShowRevokeConfirm(null)} className="px-2 py-1 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded">{t("settings.trading.cancel")}</button>
                        <button onClick={() => handleRevoke('kalshi')} disabled={revoking} className="px-2 py-1 text-[10px] font-medium bg-red-500 text-white rounded disabled:opacity-50">{revoking ? "..." : t("settings.trading.revoke")}</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hyperliquid — agent-wallet onboarding via the global
                    HyperliquidEnableModal. "Active" once approveAgent
                    lands; deposits live inside the modal so they don't
                    block the activation pill turning green. */}
                <div className="p-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <img src="/logos/hyperliquid/symbol-mint.svg" alt="Hyperliquid" className="w-5 h-5 rounded-[5px] bg-[#0f1a1f] p-0.5" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Hyperliquid</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {hlAgentApproved === false && (
                        <button
                          onClick={() => {
                            try {
                              window.dispatchEvent(new CustomEvent('frenflow:hl-enable-open'));
                            } catch { /* noop */ }
                          }}
                          className="px-4 py-1.5 text-xs font-bold text-black bg-[#97FCE4] rounded-[5px] hover:bg-[#97FCE4]/80 transition-colors"
                        >
                          {t("settings.trading.enable")}
                        </button>
                      )}
                      {hlAgentApproved === null && (
                        <span className="px-3 py-1 text-[10px] font-medium text-gray-500 bg-gray-200/40 dark:bg-white/5 rounded-[5px]">
                          ...
                        </span>
                      )}
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] ${hlAgentApproved ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <div className={`w-1.5 h-1.5 rounded-[5px] ${hlAgentApproved ? "bg-green-500" : "bg-red-500"}`} />
                        <span className={`text-[10px] font-medium ${hlAgentApproved ? "text-green-500" : "text-red-500"}`}>
                          {hlAgentApproved ? t("settings.trading.active") : t("settings.trading.inactive")}
                        </span>
                      </div>
                      {hlAgentApproved && showRevokeConfirm !== 'hyperliquid' && (
                        <button
                          onClick={() => setShowRevokeConfirm('hyperliquid')}
                          className="px-2 py-0.5 text-[10px] font-medium text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        >
                          {t("settings.trading.revoke")}
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Inline secondary state: "Approved · awaiting deposit"
                      when the agent is set but the user hasn't bridged any
                      USDC to HL yet. Tapping it reopens the modal on the
                      deposit step. */}
                  {hlAgentApproved && hlBalance < 1 && (
                    <button
                      onClick={() => {
                        try {
                          window.dispatchEvent(new CustomEvent('frenflow:hl-enable-open'));
                        } catch { /* noop */ }
                      }}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 hover:underline"
                    >
                      Deposit USDC to start trading →
                    </button>
                  )}
                  {hlAgentApproved && hlBalance > 0 && (
                    <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                      HL balance: <span className="text-emerald-600 dark:text-emerald-400 font-medium">${hlBalance.toFixed(2)}</span>
                      {hlReady === false && <span className="ml-2 text-amber-600 dark:text-amber-400">· {t("settings.trading.onboardingIncomplete")}</span>}
                    </div>
                  )}
                  {showRevokeConfirm === 'hyperliquid' && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-[5px] flex items-center justify-between">
                      <span className="text-[11px] text-red-500">{t("settings.trading.revokeHyperliquid")}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => setShowRevokeConfirm(null)} className="px-2 py-1 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded">{t("settings.trading.cancel")}</button>
                        <button
                          onClick={async () => {
                            try {
                              const res = await authFetch('/api/trading/hyperliquid/revoke', { method: 'POST' });
                              if (!res.ok) throw new Error('Failed to revoke Hyperliquid');
                              setShowRevokeConfirm(null);
                              await refreshHlStatus();
                            } catch (err: any) {
                              toast.error(err?.message || t("settings.toast.revokeHlFailed"));
                            }
                          }}
                          className="px-2 py-1 text-[10px] font-medium bg-red-500 text-white rounded disabled:opacity-50"
                        >
                          {t("settings.trading.revoke")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>


            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
              {/* Auto-Redeem */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3 lg:mb-4">
                  <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12]">
                    <FiRefreshCw className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoRedeem}
                    aria-label={t("settings.aria.toggleAutoRedeem")}
                    onClick={handleAutoRedeemToggle}
                    disabled={autoRedeemLoading || !tradingEnabled}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors ${
                      autoRedeem ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${autoRedeemLoading || !tradingEnabled ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${autoRedeem ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
                <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5 lg:mb-1">{t("settings.trading.autoRedeem")}</h3>
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                  {t("settings.trading.autoRedeemDesc")}
                </p>
              </div>

              {/* Private Trading */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3 lg:mb-4">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-[5px] flex items-center justify-center bg-purple-500/20 flex-shrink-0">
                    <FiEyeOff className="w-5 h-5 lg:w-6 lg:h-6 text-purple-500" />
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!ninjaMode}
                    aria-label={t("settings.aria.togglePrivateTrading")}
                    onClick={() => handleAdditionalSettingToggle('ninja')}
                    disabled={additionalSettingsLoading === 'ninja'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                      ninjaMode ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'ninja' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${ninjaMode ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
                <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5 lg:mb-1">{t("settings.trading.privateTrading")}</h3>
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                  {t("settings.trading.privateTradingDesc")}
                </p>
              </div>
            </div>

            {/* Slippage Tolerance — per-source tabs.
                "Default" edits the global fallback used when a source has no
                override. Each platform tab edits users.slippage_per_source[<source>],
                which trade adapters read first before falling back to default. */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-[5px] flex items-center justify-center bg-orange-500/20 flex-shrink-0">
                  <FiTrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.trading.slippage")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("settings.trading.slippageDesc")}
                  </p>
                </div>
              </div>

              {/* Source tabs */}
              <div className="flex gap-1 mb-3 overflow-x-auto -mx-1 px-1 pb-1">
                {([
                  { key: 'global' as const, label: 'Default', logo: null },
                  { key: 'polymarket' as const, label: 'Polymarket', logo: '/logos/polymarket/icon-white.png', logoBg: '#2E5CFF' },
                  { key: 'hyperliquid' as const, label: 'Hyperliquid', logo: '/logos/hyperliquid/symbol-mint.svg', logoBg: '#0f1a1f' },
                  { key: 'kalshi' as const, label: 'Kalshi', logo: '/logos/kalshi/k-icon.svg', logoBg: '#09C285' },
                  { key: 'predict' as const, label: 'Predict', logo: null },
                ]).map((tab) => {
                  const active = slippageTab === tab.key;
                  const override = tab.key !== 'global' ? slippagePerSource[tab.key] : undefined;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setSlippageTab(tab.key)}
                      className={`shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-[5px] text-xs font-medium transition-colors ${
                        active
                          ? 'bg-gray-200 dark:bg-[#25252B] text-gray-900 dark:text-white'
                          : 'bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#1A1A1A]'
                      }`}
                    >
                      {tab.logo && (
                        <img
                          src={tab.logo}
                          alt={tab.label}
                          className="w-3.5 h-3.5 rounded-[5px] p-0.5"
                          style={{ background: tab.logoBg }}
                        />
                      )}
                      <span>{tab.label}</span>
                      {tab.key !== 'global' && override != null && (
                        <span className="text-[10px] tabular-nums text-orange-500">{override}%</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active tab description */}
              {slippageTab !== 'global' && (
                <div className="mb-3 px-3 py-2 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                    {slippageTab === 'polymarket' && "Override for Polymarket CLOB market orders. Falls back to the Default tab when unset."}
                    {slippageTab === 'hyperliquid' && "Override for HIP-4 IOC orders. HL spreads move fast — bumping this avoids \"could not match\" rejects on volatile outcomes."}
                    {slippageTab === 'kalshi' && "Override for Kalshi market orders routed through DFlow. Falls back to Default when unset."}
                    {slippageTab === 'predict' && "Override for Predict.fun market orders. Falls back to Default when unset."}
                  </p>
                </div>
              )}

              {/* Slider — bound to either the global value or the source override */}
              {(() => {
                const value = slippageTab === 'global'
                  ? slippagePct
                  : (slippagePerSource[slippageTab] ?? slippagePct);
                const setLocal = (v: number) => {
                  if (slippageTab === 'global') setSlippagePct(v);
                  else setSlippagePerSource((prev) => ({ ...prev, [slippageTab]: v }));
                };
                const persist = (v: number) => handleSlippageSave(v, slippageTab === 'global' ? undefined : slippageTab);
                const hasOverride = slippageTab !== 'global' && slippagePerSource[slippageTab] != null;
                return (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input
                          type="range"
                          min={0.5}
                          max={20}
                          step={0.5}
                          value={value}
                          onChange={(e) => setLocal(Number(e.target.value))}
                          onMouseUp={() => persist(value)}
                          onTouchEnd={() => persist(value)}
                          className="w-full h-2 bg-gray-200 dark:bg-[#25252B] rounded-[5px] appearance-none cursor-pointer accent-zinc-700 dark:accent-zinc-300"
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-[10px] text-gray-400">0.5%</span>
                          <span className="text-[10px] text-gray-400">20%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] min-w-[60px] justify-center">
                        <span className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">
                          {value}%
                        </span>
                        {slippageSaving && (
                          <div className="w-3 h-3 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                    </div>
                    {/* Preset pills OR custom-input mode. The "Custom"
                        pill below opens an inline numeric input
                        (0.1-100%) for power users — penny copy
                        traders + Hyperliquid fast outcomes — who
                        need values above the slider's 20% ceiling.
                        Highlight on Custom when current value isn't
                        one of the presets so the user sees their
                        out-of-band value persisted. */}
                    {slippageCustomOpen ? (
                      <div className="flex gap-2 mt-3 items-center">
                        <input
                          type="number"
                          min={0.1}
                          max={100}
                          step={0.1}
                          autoFocus
                          value={slippageCustomDraft}
                          onChange={(e) => setSlippageCustomDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const n = Number(slippageCustomDraft);
                              if (!Number.isFinite(n) || n < 0.1 || n > 100) {
                                toast.error(t("settings.toast.slippageRange"));
                                return;
                              }
                              setLocal(n);
                              persist(n);
                              setSlippageCustomOpen(false);
                            } else if (e.key === 'Escape') {
                              setSlippageCustomOpen(false);
                            }
                          }}
                          placeholder="0.1 – 100"
                          className="flex-1 px-2.5 py-1.5 text-xs font-medium rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#25252B] text-gray-900 dark:text-white focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const n = Number(slippageCustomDraft);
                            if (!Number.isFinite(n) || n < 0.1 || n > 100) {
                              toast.error(t("settings.toast.slippageRange"));
                              return;
                            }
                            setLocal(n);
                            persist(n);
                            setSlippageCustomOpen(false);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 transition-opacity"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setSlippageCustomOpen(false)}
                          className="px-2.5 py-1.5 text-xs font-medium rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A]"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-3">
                        {[1, 2, 3, 5, 10].map((val) => (
                          <button
                            type="button"
                            key={val}
                            onClick={() => { setLocal(val); persist(val); }}
                            // 2026-05-22 (Pika): active state was
                            // `dark:bg-[#25252B]` against the card's
                            // `dark:bg-[#0f0f12]` background — the
                            // colours were nearly identical so the
                            // selected preset blended in. Switched to
                            // the canonical gray-900/white active
                            // style used by the rest of the product.
                            className={`flex-1 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${
                              value === val
                                ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                                : "bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#1A1A1A]"
                            }`}
                          >
                            {val}%
                          </button>
                        ))}
                        {/* Custom pill — highlights when current value
                            isn't a preset (incl. >20% values typed via
                            the input below). Clicking opens the input
                            with the current value pre-filled. */}
                        {(() => {
                          const isCustom = ![1, 2, 3, 5, 10].includes(value);
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setSlippageCustomDraft(String(value));
                                setSlippageCustomOpen(true);
                              }}
                              className={`flex-1 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${
                                isCustom
                                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                                  : "bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#1A1A1A]"
                              }`}
                            >
                              {isCustom ? `${value}%` : 'Custom'}
                            </button>
                          );
                        })()}
                      </div>
                    )}
                    {hasOverride && (
                      <button
                        type="button"
                        onClick={async () => {
                          // Reset = clear the per-source override so the
                          // adapter falls back to the global default.
                          setSlippageSaving(true);
                          try {
                            const res = await authFetch('/api/users/slippage', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              // null + source = clear the per-source key.
                              // The adapter falls back to default_slippage_pct
                              // until the user sets an override again.
                              body: JSON.stringify({ slippagePct: null, source: slippageTab }),
                            });
                            if (res.ok) {
                              setSlippagePerSource((prev) => {
                                const next = { ...prev };
                                delete next[slippageTab as Exclude<SlippageTab, 'global'>];
                                return next;
                              });
                              toast.success(t("settings.toast.resetDefault"));
                            } else {
                              toast.error(t("settings.toast.resetFailed"));
                            }
                          } finally {
                            setSlippageSaving(false);
                          }
                        }}
                        className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline-offset-2 hover:underline"
                      >
                        Reset to default ({slippagePct}%)
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Market Order Type — mirrors the Trading Settings panel on
                polymarket.com. Polymarket CLOB V2 market orders accept two
                time-in-force modes:
                  FAK — fill what's immediately available, cancel the rest
                  FOK — fill entirely or cancel the whole order
                Docs: docs.polymarket.com/trading/orders/overview */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                  <FiZap className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                </div>
                <div>
                  <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.trading.marketOrderType")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("settings.trading.marketOrderTypeDesc")}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {(['FAK', 'FOK'] as const).map((value) => {
                  const selected = marketOrderType === value;
                  const title = value === 'FAK' ? t("settings.trading.fakTitle") : t("settings.trading.fokTitle");
                  const desc = value === 'FAK'
                    ? t("settings.trading.fakDesc")
                    : t("settings.trading.fokDesc");
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={marketOrderTypeSaving}
                      onClick={() => handleMarketOrderTypeChange(value)}
                      className={`w-full text-left rounded-[5px] p-3 lg:p-4 border transition-colors ${
                        selected
                          ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-[#0f0f12]'
                          : 'border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50'
                      } ${marketOrderTypeSaving ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white">{title}</div>
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                        </div>
                        <span
                          className={`mt-0.5 h-4 w-4 rounded-[5px] border-2 flex-shrink-0 ${
                            selected
                              ? 'border-zinc-900 dark:border-white bg-zinc-900 dark:bg-white'
                              : 'border-gray-300 dark:border-[#25252B]'
                          }`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Buy Amounts */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-[5px] flex items-center justify-center bg-yellow-500/20 flex-shrink-0">
                  <FiStar className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-500" />
                </div>
                <div>
                  <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.trading.quickBuy")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("settings.trading.quickBuyDesc")}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm font-medium pointer-events-none">$</span>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={quickBuyInputs[i]}
                      onChange={(e) => {
                        const next = [...quickBuyInputs] as [string, string, string];
                        next[i] = e.target.value;
                        setQuickBuyInputs(next);
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") handleQuickBuySave(); }}
                      placeholder={i === 0 ? "15" : i === 1 ? "75" : "200"}
                      className="w-full pl-7 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] text-sm font-bold text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 text-center"
                    />
                  </div>
                ))}
                {/* 2026-05-22 (Pika): button toggles between
                    "primary" (gray-900/white when there are pending
                    edits) and "muted" (gray-200/dark-25 when the
                    inputs match the saved snapshot). Previously
                    it was permanently grey regardless of state,
                    so users couldn't tell when their changes were
                    ready to persist. */}
                <button
                  type="button"
                  onClick={handleQuickBuySave}
                  disabled={quickBuySaving || !quickBuyDirty}
                  className={`px-4 py-2 text-sm font-medium rounded-[5px] transition-colors disabled:opacity-40 flex-shrink-0 ${
                    quickBuyDirty
                      ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
                      : "bg-gray-200 dark:bg-[#25252B] text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {quickBuySaving ? "..." : t("settings.trading.quickBuySave")}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                {t("settings.trading.quickBuyHint")}
              </p>
            </div>
          </div>
        )}

        {/* NOTIFICATIONS SECTION */}
        {activeSection === "notifications" && (
          <div className="space-y-4 lg:space-y-6">
            {/* Global Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
              {/* Web Notifications */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-3.5 lg:p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                      <FiGlobe className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5">{t("settings.notif.webAlerts")}</h3>
                      <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.notif.webAlertsDesc")}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!webNotificationsEnabled}
                    aria-label={t("settings.aria.toggleWebAlerts")}
                    onClick={() => handleAdditionalSettingToggle('web')}
                    disabled={additionalSettingsLoading === 'web'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                      webNotificationsEnabled ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'web' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${webNotificationsEnabled ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
              </div>

              {/* Live Ticker */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-3.5 lg:p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                      <FiActivity className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5">{t("settings.notif.liveTicker")}</h3>
                      <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.notif.liveTickerDesc")}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!showTicker}
                    aria-label={t("settings.aria.toggleLiveTicker")}
                    onClick={() => handleAdditionalSettingToggle('ticker')}
                    disabled={additionalSettingsLoading === 'ticker'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                      showTicker ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'ticker' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${showTicker ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
              </div>

              {/* News Notifications */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-3.5 lg:p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                      <FiRss className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5">{t("settings.notif.newsFeed")}</h3>
                      <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.notif.newsFeedDesc")}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={newsNotificationsEnabled}
                    aria-label={t("settings.aria.toggleNewsFeed")}
                    onClick={() => setNewsNotificationsEnabled(!newsNotificationsEnabled)}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                      newsNotificationsEnabled ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${newsNotificationsEnabled ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
              </div>

              {/* Price Alerts Toggle */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-3.5 lg:p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                      <FiTarget className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5">{t("settings.notif.priceAlerts")}</h3>
                      <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.notif.priceAlertsDesc")}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!webBellPriceAlerts}
                    aria-label={t("settings.aria.togglePriceAlerts")}
                    onClick={() => handleAdditionalSettingToggle('bell_price_alerts')}
                    disabled={additionalSettingsLoading === 'bell_price_alerts'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                      webBellPriceAlerts ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'bell_price_alerts' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${webBellPriceAlerts ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
              </div>
              {/* Push Notifications */}
              {pushSupported && (
                <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-3.5 lg:p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 lg:gap-4">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                        <FiBell className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-300" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white mb-0.5">{t("settings.notif.push")}</h3>
                        <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                          {pushError ? <span className="text-red-500">{pushError}</span> : pushState === "subscribed" ? t("settings.notif.pushSubscribed") : pushState === "denied" ? t("settings.notif.pushDenied") : t("settings.notif.pushNative", { state: pushState })}
                        </p>
                        {/* 2026-05-22: Brave-specific hint when push fails.
                            Brave disables Google FCM by default; without
                            this hint the user gets "Registration failed -
                            push service error" with no recovery path.
                            Renders only when we detected Brave AND push
                            either errored OR is in "unsubscribed" state
                            (so the hint also helps a Brave user who's
                            about to click subscribe). */}
                        {isBrave && (pushError || pushState === "unsubscribed") && (
                          <div className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-[5px] bg-amber-500/10 border border-amber-500/30">
                            <span className="text-base leading-tight">🦁</span>
                            <div className="flex-1 min-w-0 text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                              {t("settings.notif.bravePushHint")}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (pushState === "subscribed") {
                          await pushUnsubscribe();
                          await authFetch("/api/users/settings", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ notify_push_enabled: false }),
                          });
                        } else {
                          const success = await pushSubscribe();
                          if (success) {
                            await authFetch("/api/users/settings", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ notify_push_enabled: true }),
                            });
                          }
                        }
                      }}
                      disabled={pushLoading}
                      type="button"
                      role="switch"
                      aria-checked={pushState === "subscribed"}
                      aria-label={t("settings.aria.togglePush")}
                      className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 items-center rounded-[5px] transition-colors flex-shrink-0 ${
                        pushState === "subscribed" ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                      } ${pushLoading ? "opacity-50" : ""}`}
                    >
                      <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${pushState === "subscribed" ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Web App Notifications */}
            <div>
              <div className="flex items-center gap-2 mb-2 lg:mb-3">
                <FiBell className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500 dark:text-gray-400" />
                <span className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.notif.webApp")}</span>
              </div>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mb-3 lg:mb-4">
                {t("settings.notif.webAppDesc")}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 lg:gap-3">
                {/* Copytrading Toggle */}
                <div className="flex items-center justify-between p-3 lg:p-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors">
                  <div className="flex-1 min-w-0 mr-3">
                    <h3 className="font-inter-tight font-medium text-sm text-gray-900 dark:text-white">{t("settings.notif.copytrading")}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{t("settings.notif.copytradingDesc")}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!webBellCopytrading}
                    aria-label={t("settings.aria.toggleCopytrading")}
                    onClick={() => handleAdditionalSettingToggle('bell_copytrading')}
                    disabled={additionalSettingsLoading === 'bell_copytrading'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 flex-shrink-0 items-center rounded-[5px] transition-colors ${
                      webBellCopytrading ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'bell_copytrading' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${webBellCopytrading ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>

                {/* Wallet Tracker Toggle */}
                <div className="flex items-center justify-between p-3 lg:p-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors">
                  <div className="flex-1 min-w-0 mr-3">
                    <h3 className="font-inter-tight font-medium text-sm text-gray-900 dark:text-white">{t("settings.notif.walletTracker")}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{t("settings.notif.walletTrackerDesc")}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!webBellWalletTracker}
                    aria-label={t("settings.aria.toggleWalletTracker")}
                    onClick={() => handleAdditionalSettingToggle('bell_wallet_tracker')}
                    disabled={additionalSettingsLoading === 'bell_wallet_tracker'}
                    className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 flex-shrink-0 items-center rounded-[5px] transition-colors ${
                      webBellWalletTracker ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                    } ${additionalSettingsLoading === 'bell_wallet_tracker' ? "opacity-50" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${webBellWalletTracker ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Telegram Notifications */}
            <div>
              <div className="flex items-center gap-2 mb-2 lg:mb-3">
                <FaTelegram className="w-4 h-4 lg:w-5 lg:h-5 text-[#229ED9]" />
                <span className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.notif.telegram")}</span>
              </div>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mb-3 lg:mb-4">
                {t("settings.notif.telegramDesc")}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 lg:gap-3">
                {[
                  { key: "copy_trade" as const, label: t("settings.notif.copyTrade"), desc: t("settings.notif.copyTradeDesc") },
                  { key: "copy_skips" as const, label: t("settings.notif.copySkips"), desc: t("settings.notif.copySkipsDesc") },
                  { key: "limit_filled" as const, label: t("settings.notif.limitFilled"), desc: t("settings.notif.limitFilledDesc") },
                  { key: "limit_expired" as const, label: t("settings.notif.limitExpired"), desc: t("settings.notif.limitExpiredDesc") },
                  { key: "position_resolved" as const, label: t("settings.notif.resolved"), desc: t("settings.notif.resolvedDesc") },
                  { key: "low_balance" as const, label: t("settings.notif.lowBalance"), desc: t("settings.notif.lowBalanceDesc") },
                  { key: "high_slippage" as const, label: t("settings.notif.slippage"), desc: t("settings.notif.slippageDesc") },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-3 lg:p-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors">
                    <div className="flex-1 min-w-0 mr-3">
                      <h3 className="font-inter-tight font-medium text-sm text-gray-900 dark:text-white">{item.label}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{item.desc}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={notifications[item.key]}
                      aria-label={t("settings.aria.toggleItem", { label: item.label })}
                      onClick={() => handleNotificationToggle(item.key)}
                      disabled={notificationLoading === item.key}
                      className={`relative inline-flex h-6 w-11 lg:h-7 lg:w-12 flex-shrink-0 items-center rounded-[5px] transition-colors ${
                        notifications[item.key] ? "bg-zinc-900 dark:bg-white" : "bg-gray-200 dark:bg-[#25252B]"
                      } ${notificationLoading === item.key ? "opacity-50" : ""}`}
                    >
                      <span className={`inline-block h-4 w-4 lg:h-5 lg:w-5 transform rounded-[5px] shadow-md transition-transform ${notifications[item.key] ? "translate-x-6 bg-white dark:bg-zinc-900" : "translate-x-1 bg-white"}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* WALLET SECTION */}
        {activeSection === "wallet" && (
          <>
          {/* ===== Unified Balance + Move Funds ===== */}
          <UnifiedBalanceCard onMoveFunds={() => setShowMoveFundsModal(true)} />

          {/* ===== Platform Wallets — organized by prediction market ===== */}
          <div className="mt-4 space-y-3">
            {/* Polymarket */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-3">
                <img src={STATIC_ASSETS.polymarket.iconBlue} alt="" className="w-7 h-7 rounded-[5px]" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">Polymarket</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t("settings.wallet.pUsdPolygon")}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {polygonBalance !== null ? `$${polygonBalance.toFixed(2)}` : '—'}
                </span>
              </div>

              {polymarketTradingAddress ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(polymarketTradingAddress);
                      setCopiedSafe(true);
                      setTimeout(() => setCopiedSafe(false), 2000);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] text-xs group hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                  >
                    <span className="font-mono text-gray-500 dark:text-gray-400 truncate">
                      {polymarketTradingAddress}
                    </span>
                    {copiedSafe ? (
                      <FiCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0 ml-2" />
                    ) : (
                      <FiCopy className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                    )}
                  </button>

                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <a
                      href={`https://polygonscan.com/address/${polymarketTradingAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]"
                    >
                      <FiExternalLink className="w-3 h-3" />
                      Polygonscan
                    </a>
                    <button
                      onClick={() => {
                        try {
                          window.dispatchEvent(new CustomEvent('frenflow:pm-withdraw-open'));
                        } catch { /* noop */ }
                      }}
                      disabled={!tradingEnabled || (polygonBalance ?? 0) < 1}
                      title={!tradingEnabled ? t("settings.wallet.enableFirst") : (polygonBalance ?? 0) < 1 ? t("settings.wallet.nothingToWithdraw") : ''}
                      className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-[5px] border border-emerald-500/30"
                    >
                      {t("settings.wallet.withdraw")}
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => { window.location.href = '/settings/trading'; }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-[5px] border border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400 transition-colors"
                >
                  {t("settings.wallet.enablePolymarket")}
                </button>
              )}

              {/* Stranded-funds rescue: if USDC.e/USDC/pUSD is sitting on
                  the login EOA instead of the deposit wallet, show an
                  inline notice + one-tap move (same flow as the floating
                  EoaSweepBanner). Persistent here — survives banner
                  dismissal so users can always recover from settings. */}
              <EoaSweepInline />
            </div>

            {/* Predict.fun wallet card — gated by ENABLE_PREDICT. EOA on BNB Chain (USDT). */}
            {predictEnabled && (
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-7 h-7 rounded-[5px] bg-[#8B5CF6] flex items-center justify-center"><span className="text-sm font-black text-white leading-none">P</span></div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">Predict.fun</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">USDT · BNB Chain</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {bnbUsdtBalance !== null && bnbUsdtBalance !== undefined ? `$${bnbUsdtBalance.toFixed(2)}` : '—'}
                </span>
              </div>

              {bnbWalletAddress ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(bnbWalletAddress);
                      setCopiedBnbWallet(true);
                      setTimeout(() => setCopiedBnbWallet(false), 2000);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] text-xs group hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                  >
                    <span className="font-mono text-gray-500 dark:text-gray-400 truncate">{bnbWalletAddress}</span>
                    {copiedBnbWallet ? (
                      <FiCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0 ml-2" />
                    ) : (
                      <FiCopy className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                    )}
                  </button>

                  <a
                    href={`https://bscscan.com/address/${bnbWalletAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <FiExternalLink className="w-3 h-3" />
                    BscScan
                  </a>
                </>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">—</p>
              )}
            </div>
            )}

            {/* Kalshi / DFlow */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-7 h-7 rounded-[5px] bg-[#09C285] flex items-center justify-center"><span className="text-sm font-black text-white leading-none">K</span></div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">Kalshi</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t("settings.wallet.usdcSolana")}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {solanaBalance !== null ? `$${solanaBalance.toFixed(2)}` : '—'}
                </span>
              </div>

              {solanaAddress ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(solanaAddress);
                      setCopiedSolWallet(true);
                      setTimeout(() => setCopiedSolWallet(false), 2000);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] text-xs group hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                  >
                    <span className="font-mono text-gray-500 dark:text-gray-400 truncate">
                      {solanaAddress}
                    </span>
                    {copiedSolWallet ? (
                      <FiCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0 ml-2" />
                    ) : (
                      <FiCopy className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                    )}
                  </button>

                  <a
                    href={`https://solscan.io/account/${solanaAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <FiExternalLink className="w-3 h-3" />
                    Solscan
                  </a>
                </>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                  {t("settings.wallet.loadingSolana")}
                </p>
              )}
            </div>

            {/* Hyperliquid — funds live on HyperCore (HL's L1); the
                "wallet address" we surface is the user's Privy embedded
                EOA on Arbitrum, which is both their HL master account
                and the destination for cross-chain deposits to
                Bridge2. Same address as Polymarket's master EOA (Privy
                shares the EOA across all EVM chains). */}
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-3">
                <img src="/logos/hyperliquid/symbol-mint.svg" alt="" className="w-7 h-7 rounded-[5px] bg-[#0f1a1f] p-1" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">Hyperliquid</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t("settings.wallet.usdcHypercore")}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {hlBalance !== null ? `$${hlBalance.toFixed(2)}` : '—'}
                </span>
              </div>

              {currentWalletAddress && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(currentWalletAddress);
                    setCopiedSafe(true);
                    setTimeout(() => setCopiedSafe(false), 2000);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] text-xs group hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                >
                  <span className="font-mono text-gray-500 dark:text-gray-400 truncate">
                    {currentWalletAddress}
                  </span>
                  {copiedSafe ? (
                    <FiCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0 ml-2" />
                  ) : (
                    <FiCopy className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                  )}
                </button>
              )}

              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`https://arbiscan.io/address/${currentWalletAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]"
                >
                  <FiExternalLink className="w-3 h-3" />
                  Arbiscan
                </a>
                <button
                  onClick={() => {
                    try {
                      window.dispatchEvent(new CustomEvent('frenflow:hl-withdraw-open'));
                    } catch { /* noop */ }
                  }}
                  disabled={!hlAgentApproved || hlBalance < 1}
                  title={!hlAgentApproved ? t("settings.wallet.enableFirst") : hlBalance < 1 ? t("settings.wallet.nothingToWithdraw") : ''}
                  className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-[5px] border border-emerald-500/30"
                >
                  {t("settings.wallet.withdrawArbitrum")}
                </button>
              </div>

              {hlAgentApproved === false && (
                <button
                  onClick={() => {
                    try {
                      window.dispatchEvent(new CustomEvent('frenflow:hl-enable-open'));
                    } catch { /* noop */ }
                  }}
                  className="mt-2 w-full text-xs text-emerald-600 dark:text-emerald-400 hover:underline text-center"
                >
                  {t("settings.wallet.enableHyperliquid")}
                </button>
              )}
            </div>
          </div>

          {/* ===== Export Private Keys ===== */}
          {currentWalletAddress && (
          <div className="mt-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-7 h-7 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center flex-shrink-0">
                <FiKey className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <h3 className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white">{t("settings.wallet.exportKeys")}</h3>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4 ml-10">
              {t("settings.wallet.exportKeysDesc")}
            </p>

            <div className="space-y-2">
              {/* EVM Private Key */}
              <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
                <div className="flex items-center gap-1 flex-shrink-0">
                  <img src="https://assets.coingecko.com/coins/images/279/small/ethereum.png" alt="ETH" className="w-4 h-4 rounded-[5px]" />
                  <img src="https://assets.coingecko.com/coins/images/4713/small/polygon.png" alt="Polygon" className="w-4 h-4 rounded-[5px]" />
                  <img src="https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png" alt="BNB" className="w-4 h-4 rounded-[5px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-white">{t("settings.wallet.evmWallet")}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 font-mono truncate">{currentWalletAddress}</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportWallet({ address: currentWalletAddress })}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/5 rounded-[5px] transition-colors"
                >
                  <FiKey className="w-3 h-3" />
                  {t("settings.wallet.export")}
                </button>
              </div>

              {/* Solana Private Key */}
              {solanaAddress && (
              <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
                <div className="flex items-center gap-1 flex-shrink-0">
                  <img src="https://assets.coingecko.com/coins/images/4128/small/solana.png" alt="Solana" className="w-4 h-4 rounded-[5px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-white">{t("settings.wallet.solanaWallet")}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 font-mono truncate">{solanaAddress}</p>
                </div>
                <button
                  type="button"
                  onClick={() => exportWallet({ address: solanaAddress })}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/5 rounded-[5px] transition-colors"
                >
                  <FiKey className="w-3 h-3" />
                  {t("settings.wallet.export")}
                </button>
              </div>
              )}
            </div>

            {/* 2026-05-22 (Pika): private-key warning now uses the
                standard amber banner pattern (bg + border + padded
                box) instead of free-floating low-contrast grey
                text. Critical security-impact copy should be
                impossible to miss; the previous layout buried it
                below the export buttons and read like a footnote. */}
            <div className="mt-4 flex items-start gap-2 rounded-[5px] border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-700 dark:border-amber-300/20 dark:bg-amber-400/10 dark:text-amber-300">
              <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div className="space-y-1 leading-relaxed">
                <p>{t("settings.wallet.warnPrivKey")}</p>
                <p className="opacity-80">{t("settings.wallet.warnNoStore")}</p>
              </div>
            </div>
          </div>
          )}

          </>
        )}

        {/* CONNECTIONS SECTION */}
        {activeSection === "connections" && (
          <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-3 lg:mb-4">
              <div className="w-8 h-8 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center flex-shrink-0">
                <LinkIcon size={18} isHovered className="text-gray-500 dark:text-gray-400" />
              </div>
              <h3 className="font-inter-tight font-bold text-sm lg:text-base text-gray-900 dark:text-white">{t("settings.connections.title")}</h3>
            </div>

            <div className="space-y-2.5 lg:space-y-3">
              {/* Google */}
              <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <svg className="w-5 h-5 lg:w-6 lg:h-6 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">Google</p>
                  {user?.google ? (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 truncate">{user.google.email}</p>
                  ) : (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.connections.googleBenefit")}</p>
                  )}
                </div>
                {user?.google ? (
                  <FiCheck className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <button onClick={() => linkGoogle()} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#2a2a2f] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#1f1f24] transition-colors text-gray-700 dark:text-gray-300">
                    {t("settings.connections.connect")}
                  </button>
                )}
              </div>

              {/* X (Twitter) */}
              <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-gray-900 dark:text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">X (Twitter)</p>
                  {user?.twitter ? (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">@{user.twitter.username}</p>
                  ) : (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.connections.twitterBenefit")}</p>
                  )}
                </div>
                {user?.twitter ? (
                  <FiCheck className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <button onClick={() => linkTwitter()} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#2a2a2f] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#1f1f24] transition-colors text-gray-700 dark:text-gray-300">
                    {t("settings.connections.connect")}
                  </button>
                )}
              </div>

              {/* Telegram */}
              <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-[#229ED9] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">Telegram</p>
                  {user?.telegram ? (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">@{user.telegram.username}</p>
                  ) : (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.connections.telegramBenefit")}</p>
                  )}
                </div>
                {user?.telegram ? (
                  <FiCheck className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <button onClick={() => linkTelegram()} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#2a2a2f] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#1f1f24] transition-colors text-gray-700 dark:text-gray-300">
                    {t("settings.connections.connect")}
                  </button>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <FiMail className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">{t("settings.connections.email")}</p>
                  {user?.email ? (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 truncate">{user.email.address}</p>
                  ) : (
                    <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">{t("settings.connections.emailBenefit")}</p>
                  )}
                </div>
                {user?.email ? (
                  <FiCheck className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <button onClick={() => linkEmail()} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#2a2a2f] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#1f1f24] transition-colors text-gray-700 dark:text-gray-300">
                    {t("settings.connections.connect")}
                  </button>
                )}
              </div>

              {/* External Wallet */}
              <div className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <WalletIcon size={20} isHovered className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">{t("settings.connections.externalWallet")}</p>
                  <p className="text-xs lg:text-sm text-gray-400 dark:text-gray-500">{t("settings.connections.externalWalletDesc")}</p>
                </div>
                <button onClick={() => linkWallet()} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#2a2a2f] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#1f1f24] transition-colors text-gray-700 dark:text-gray-300">
                  {t("settings.connections.connect")}
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* Move Funds Modal */}
      <MoveFundsModal
        isOpen={showMoveFundsModal}
        onClose={() => setShowMoveFundsModal(false)}
      />
    </div>
  );
}
